import { setTelemetryUser } from '@/otel/user-context';
import { getMe } from '@/services/api';
import { MeResponse } from '@/services/types';
import { useEffect, useMemo, useState } from 'react';

type State =
  | { status: 'loading'; requestKey: string | undefined; me: null; error: null }
  | { status: 'ready'; requestKey: string | undefined; me: MeResponse; error: null }
  | { status: 'error'; requestKey: string | undefined; me: null; error: Error };

export function useMe(requestKey?: string) {
  const [state, setState] = useState<State>({
    status: 'loading',
    requestKey: undefined,
    me: null,
    error: null,
  });

  useEffect(() => {
    const ac = new AbortController();
    let alive = true;
    setState({ status: 'loading', requestKey, me: null, error: null });
    getMe({ signal: ac.signal })
      .then((me) => {
        setTelemetryUser(me.isAuthenticated ? me.userId : undefined);
        if (alive) setState({ status: 'ready', requestKey, me, error: null });
      })
      .catch((err) => {
        if (!alive || ac.signal.aborted) return;
        setState({ status: 'error', requestKey, me: null, error: err as Error });
      });

    return () => {
      alive = false;
      ac.abort();
    };
  }, [requestKey]);

  const isCurrent = state.requestKey === requestKey;
  const isLoading = !isCurrent || state.status === 'loading';
  const isAuthenticated = isCurrent && state.status === 'ready' && state.me.isAuthenticated;

  const email = isCurrent && state.status === 'ready' ? (state.me.email ?? '') : '';

  const hasRole = useMemo(() => {
    return (role: string) =>
      isCurrent && state.status === 'ready' && (state.me.roles ?? []).includes(role);
  }, [isCurrent, state]);

  return {
    isLoading,
    isAuthenticated,
    me: isCurrent && state.status === 'ready' ? state.me : null,
    email,
    hasRole,
    error: isCurrent && state.status === 'error' ? state.error : null,
  };
}
